/**
 * At-rest encryption for every ledger replica.
 *
 * Public blockchains are usually stored in the clear, and the result is
 * that anyone who gets a copy of the disk - a seized laptop, a subpoenaed
 * hosting provider, a stolen phone - gets a permanent, offline, fully
 * indexed record of everything that node ever saw. For a network whose
 * users are specifically people that banks and governments have shut out,
 * that is not an acceptable default.
 *
 * So the replica is sealed. Every record is AES-256-GCM under a key
 * derived from the node's own seed, with the record key as authenticated
 * associated data so records cannot be swapped, and a per-record random
 * nonce. Verifiability is not affected: signatures, hashes and the DAG
 * structure are all intact inside the envelope, and any node can open its
 * own replica and re-verify the entire history from genesis. The ledger is
 * public *by protocol* - anybody may fetch, replay and audit it - without
 * being public *by accident* on every disk it has ever touched.
 *
 * Note the honest limit: this protects a replica at rest, not the
 * amounts and addresses inside a vertex, which every validator must read
 * to check the arithmetic. Sender-recipient privacy is a memo-level and
 * future-work concern, documented in WHITEPAPER.md section 8.
 */

import { gcm } from '@noble/ciphers/aes';
import { fromHex, randomBytes, toArrayBuffer, toHex, utf8, type Hex } from './bytes.ts';
import { vaultKey } from './keys.ts';

export interface SealedRecord {
  /** Envelope version, so the format can be rotated without data loss. */
  v: 1;
  n: Hex;
  c: Hex;
}

export class Vault {
  private key: Uint8Array;
  readonly scope: string;

  constructor(seed: Uint8Array, scope = 'ledger') {
    this.key = vaultKey(seed, scope);
    this.scope = scope;
  }

  seal(recordKey: string, plaintext: Uint8Array): SealedRecord {
    const nonce = randomBytes(12);
    const aad = utf8(`face/vault/v1/${this.scope}/${recordKey}`);
    const ct = gcm(this.key, nonce, aad).encrypt(plaintext);
    return { v: 1, n: toHex(nonce), c: toHex(ct) };
  }

  open(recordKey: string, record: SealedRecord): Uint8Array {
    if (record?.v !== 1) throw new Error('unsupported vault envelope');
    const aad = utf8(`face/vault/v1/${this.scope}/${recordKey}`);
    return gcm(this.key, fromHex(record.n), aad).decrypt(fromHex(record.c));
  }

  sealJson(recordKey: string, value: unknown): SealedRecord {
    return this.seal(recordKey, utf8(JSON.stringify(value)));
  }

  openJson<T = unknown>(recordKey: string, record: SealedRecord): T {
    return JSON.parse(new TextDecoder().decode(this.open(recordKey, record))) as T;
  }
}

/**
 * Passphrase-sealed keystore for the wallet's own signing seed. Uses
 * PBKDF2-SHA256 via WebCrypto (available identically in browsers, Workers
 * and Node) so there is no native dependency to install or trust.
 */
export interface Keystore {
  v: 1;
  kdf: 'pbkdf2-sha256';
  iterations: number;
  salt: Hex;
  nonce: Hex;
  ciphertext: Hex;
  address: string;
}

const KDF_ITERATIONS = 310_000;

async function deriveFromPassphrase(passphrase: string, salt: Uint8Array, iterations: number): Promise<Uint8Array> {
  const base = await crypto.subtle.importKey('raw', toArrayBuffer(utf8(passphrase)), 'PBKDF2', false, ['deriveBits']);
  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', salt: toArrayBuffer(salt), iterations, hash: 'SHA-256' },
    base,
    256,
  );
  return new Uint8Array(bits);
}

export async function sealKeystore(seed: Uint8Array, passphrase: string, address: string): Promise<Keystore> {
  const salt = randomBytes(16);
  const nonce = randomBytes(12);
  const key = await deriveFromPassphrase(passphrase, salt, KDF_ITERATIONS);
  const ct = gcm(key, nonce, utf8('face/keystore/v1/' + address)).encrypt(seed);
  return {
    v: 1,
    kdf: 'pbkdf2-sha256',
    iterations: KDF_ITERATIONS,
    salt: toHex(salt),
    nonce: toHex(nonce),
    ciphertext: toHex(ct),
    address,
  };
}

export async function openKeystore(ks: Keystore, passphrase: string): Promise<Uint8Array> {
  if (ks?.v !== 1 || ks.kdf !== 'pbkdf2-sha256') throw new Error('unsupported keystore format');
  const key = await deriveFromPassphrase(passphrase, fromHex(ks.salt), ks.iterations);
  try {
    return gcm(key, fromHex(ks.nonce), utf8('face/keystore/v1/' + ks.address)).decrypt(fromHex(ks.ciphertext));
  } catch {
    throw new Error('wrong passphrase');
  }
}
